import { useState } from 'react';
import { ArrowDown, ArrowUp, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import type { ProductSpec, ProductWorkbench, RateView, WorkbenchProduct } from '@/types/product';
import {
  addLine,
  hoursToInput,
  lineReferences,
  moveLine,
  removeLine,
  setHours,
  updateLine,
} from './productEditorState';
import FormulaInput from './FormulaInput';
import { calculate, hours, money } from './workbenchPricing';
import RateCombobox from './RateCombobox';

interface Props {
  product: WorkbenchProduct;
  busy: boolean;
  rates: RateView[];
  ratesByKey: Record<string, number>;
  data: ProductWorkbench;
  columns: string;
  onChange: (spec: ProductSpec) => void;
  onSelectCell: (cell: { productId: string; lineId: string; variantId: string }) => void;
}
export default function WorkbenchHoursGrid({
  product,
  busy,
  rates,
  ratesByKey,
  data,
  columns,
  onChange,
  onSelectCell,
}: Props) {
  const { spec, computation } = product;
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const lineToDelete = spec.lines.find((line) => line.id === deleteId);
  const refs = lineToDelete ? lineReferences(spec, lineToDelete.code) : [];
  return (
    <>
      <div
        className="grid items-center border-b bg-muted/50 text-xs font-semibold"
        style={{ gridTemplateColumns: columns }}
      >
        <div className="grid grid-cols-[minmax(0,1fr)_140px] gap-2 px-4 py-2">
          <span>Werkzaamheden</span>
          <span className="text-right">Tarief / uur</span>
        </div>
        {spec.variants.map((variant) => (
          <div key={variant.id} className="px-4 py-2 text-right">
            {variant.name} · uren
          </div>
        ))}
        <div />
      </div>
      {spec.lines.map((line, index) => (
        <div
          key={line.id}
          className="grid items-start border-b bg-card"
          style={{ gridTemplateColumns: columns }}
        >
          <div className="grid grid-cols-[minmax(0,1fr)_140px] items-center gap-2 px-4 py-2">
            <Input
              aria-label={`${product.name} · regel ${index + 1} · werkzaamheid`}
              disabled={busy}
              className="h-8 min-w-0 border-transparent px-0 focus-visible:border-input"
              placeholder="Werkzaamheid"
              value={line.description}
              onChange={(event) =>
                onChange(updateLine(spec, line.id, { description: event.target.value }))
              }
            />
            <RateCombobox
              label={`Tarief voor ${product.name} · ${line.description || `regel ${index + 1}`}`}
              disabled={busy}
              value={line.rateKey}
              rates={rates}
              ratesByKey={ratesByKey}
              onChange={(rateKey) => onChange(updateLine(spec, line.id, { rateKey }))}
            />
          </div>
          {spec.variants.map((variant) => {
            const computed = computation.variants.find((item) => item.variantId === variant.id)!;
            const error = computed.errors.find((item) => item.code === line.code)?.message;
            return (
              <div
                key={variant.id}
                className="px-4 py-2"
                onFocusCapture={(event) =>
                  // React laat focus uit de formuledialoog (een portal) hier doorbubbelen.
                  event.currentTarget.contains(event.target) &&
                  onSelectCell({
                    productId: product.id,
                    lineId: line.id,
                    variantId: variant.id,
                  })
                }
              >
                <fieldset disabled={busy} className="min-w-0">
                  <FormulaInput
                    value={hoursToInput(line.hours[variant.id])}
                    onChange={(value) => onChange(setHours(spec, line.id, variant.id, value))}
                    ariaLabel={`${product.name} · ${line.description || line.code} · ${variant.name} · uren`}
                    lineLabel={line.description}
                    variantName={variant.name}
                    rateLabel={rates.find((rate) => rate.key === line.rateKey)?.name}
                    options={spec.lines
                      .filter((item) => item.id !== line.id)
                      .map((item) => ({
                        code: item.code,
                        description: item.description,
                        kind: String(item.hours[variant.id]).startsWith('=') ? 'formula' : 'manual',
                        hours: computed.hoursByCode[item.code],
                      }))}
                    resolvedHours={computed.hoursByCode[line.code]}
                    resolvedAmount={money(computed.lineTotalCents[line.code])}
                    formulaSummary={
                      String(line.hours[variant.id]).startsWith('=')
                        ? `${hours(computed.hoursByCode[line.code] ?? 0)} uur`
                        : undefined
                    }
                    error={error}
                    showResult={false}
                    onPreview={async (formula) => {
                      const preview = calculate(
                        setHours(spec, line.id, variant.id, formula),
                        ratesByKey,
                        data
                      ).variants.find((item) => item.variantId === variant.id)!;
                      return {
                        hours: preview.hoursByCode[line.code],
                        amount: money(preview.lineTotalCents[line.code]),
                        error: preview.errors.find((item) => item.code === line.code)?.message,
                      };
                    }}
                  />
                </fieldset>
                {error && (
                  <p role="alert" className="mt-1 text-xs text-destructive">
                    {error}
                  </p>
                )}
              </div>
            );
          })}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="mt-2"
                disabled={busy}
                aria-label={`Acties voor regel ${index + 1} van ${product.name}`}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled={index === 0}
                onSelect={() => onChange(moveLine(spec, line.id, index - 1))}
              >
                <ArrowUp />
                Omhoog
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={index === spec.lines.length - 1}
                onSelect={() => onChange(moveLine(spec, line.id, index + 1))}
              >
                <ArrowDown />
                Omlaag
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setDeleteId(line.id)} className="text-destructive">
                <Trash2 />
                Regel verwijderen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-3 px-4 py-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={busy || !rates.length}
          onClick={() => onChange(addLine(spec, rates[0]?.key ?? ''))}
        >
          <Plus className="mr-1 h-4 w-4" />
          Regel toevoegen
        </Button>
        {!spec.lines.length && (
          <span className="text-xs text-muted-foreground">Voeg de eerste werkzaamheid toe.</span>
        )}
        {!!spec.components?.length && (
          <span className="text-xs text-muted-foreground">
            Inclusief {spec.components.length} vastgelegde bundelonderdelen. Beheer ze in
            Productdetails.
          </span>
        )}
      </div>
      <div
        className="grid items-center border-t bg-muted/50"
        style={{ gridTemplateColumns: columns }}
      >
        <div className="px-4 py-3 text-sm font-semibold">
          Productprijs
          {spec.cost.mode === 'markup' && (
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              uit kostprijs + opslag
            </span>
          )}
        </div>
        {computation.variants.map((variant) => (
          <div key={variant.variantId} className="px-4 py-3 text-right">
            <div className="text-xs text-muted-foreground">{hours(variant.totalHours)} uur</div>
            <div className="text-base font-semibold tabular-nums">
              {variant.errors.length ? '—' : money(variant.priceCents)}
            </div>
          </div>
        ))}
        <div />
      </div>
      <Dialog open={Boolean(deleteId)} onOpenChange={(next) => !next && setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regel verwijderen?</DialogTitle>
          </DialogHeader>
          <p className="text-sm">
            {lineToDelete?.description || 'Deze werkzaamheid'} wordt uit alle varianten verwijderd.
          </p>
          {refs.length > 0 && (
            <p role="alert" className="text-sm text-destructive">
              De formule van {refs.join(', ')} gebruikt deze regel. Pas die formule eerst aan
              voordat je de regel verwijdert.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Annuleren
            </Button>
            <Button
              variant="destructive"
              disabled={refs.length > 0}
              onClick={() => {
                if (deleteId) onChange(removeLine(spec, deleteId));
                setDeleteId(null);
              }}
            >
              Verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
